import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import apiClient from 'enrollment-platform/api/enrollment-platform-client/api-client'
import { parseAttachmentFilename } from 'helpers/parse-attachment-filename'

import type {
  GetCarrierDetailRequestDto,
  GetCarrierDetailResponseDto,
  GetCarrierSummaryResponseDto,
  GetPaymentBotAttemptExportRequestDto,
  GetPaymentBotDashboardRequestDto,
  GetRecentFailuresRequestDto,
  GetRecentFailuresResponseDto,
} from './dto/get-payment-bot-dashboard.dto'

export interface PaymentBotAttemptExportFile {
  blob: Blob
  /**
   * Filename the server named the attachment. Null when `Content-Disposition` is
   * missing or unparseable — the caller supplies its own fallback then.
   */
  filename: string | null
}

export const downloadPaymentBotAttempts = async (
  params: GetPaymentBotAttemptExportRequestDto
): Promise<PaymentBotAttemptExportFile> => {
  const response = await apiClient.get<Blob>(
    '/payment-bot/dashboard/attempts/export',
    { params, responseType: 'blob' }
  )
  const contentDisposition = response.headers?.['content-disposition']
  return {
    blob: response.data,
    filename:
      typeof contentDisposition === 'string'
        ? parseAttachmentFilename(contentDisposition)
        : null,
  }
}

export const getPaymentBotCarrierSummary = async (
  params: GetPaymentBotDashboardRequestDto
): Promise<GetCarrierSummaryResponseDto> => {
  const response = await apiClient.get<GetCarrierSummaryResponseDto>(
    '/payment-bot/dashboard/carrier-summary',
    { params }
  )
  return response.data
}

export const getPaymentBotCarrierDetail = async (
  params: GetCarrierDetailRequestDto
): Promise<GetCarrierDetailResponseDto> => {
  const response = await apiClient.get<GetCarrierDetailResponseDto>(
    '/payment-bot/dashboard/carrier-detail',
    { params }
  )
  return response.data
}

export const getPaymentBotRecentFailures = async (
  params: GetRecentFailuresRequestDto
): Promise<GetRecentFailuresResponseDto> => {
  const response = await apiClient.get<GetRecentFailuresResponseDto>(
    '/payment-bot/dashboard/recent-failures',
    { params }
  )
  return response.data
}

export const usePaymentBotCarrierSummaryQuery = (
  params: GetPaymentBotDashboardRequestDto,
  options?: Omit<
    UseQueryOptions<
      GetCarrierSummaryResponseDto,
      Error,
      GetCarrierSummaryResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) =>
  useQuery<GetCarrierSummaryResponseDto, Error, GetCarrierSummaryResponseDto>({
    queryKey: ['payment-bot-dashboard', 'carrier-summary', params],
    queryFn: () => getPaymentBotCarrierSummary(params),
    staleTime: 60_000,
    ...options,
  })

export const usePaymentBotCarrierDetailQuery = (
  params: GetCarrierDetailRequestDto,
  options?: Omit<
    UseQueryOptions<
      GetCarrierDetailResponseDto,
      Error,
      GetCarrierDetailResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) =>
  useQuery<GetCarrierDetailResponseDto, Error, GetCarrierDetailResponseDto>({
    queryKey: ['payment-bot-dashboard', 'carrier-detail', params],
    queryFn: () => getPaymentBotCarrierDetail(params),
    staleTime: 60_000,
    ...options,
  })

export const usePaymentBotRecentFailuresQuery = (
  params: GetRecentFailuresRequestDto,
  options?: Omit<
    UseQueryOptions<
      GetRecentFailuresResponseDto,
      Error,
      GetRecentFailuresResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) =>
  useQuery<GetRecentFailuresResponseDto, Error, GetRecentFailuresResponseDto>({
    queryKey: ['payment-bot-dashboard', 'recent-failures', params],
    queryFn: () => getPaymentBotRecentFailures(params),
    staleTime: 60_000,
    ...options,
  })
